import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import useVoyagerStore from '../../stores/useVoyagerStore';
import type { LandingAgentTurn } from '../../stores/slices/landingAgentSlice';
import { progressOf } from '../../stores/slices/progress';
import { WorkingSteps, hasSteps } from './simple/WorkingBlock';

export const LINK_SX = {
  fontSize: 12.5, color: 'var(--ink-3)', textDecoration: 'underline', textUnderlineOffset: '2px',
  textDecorationColor: 'var(--line)', borderRadius: '4px', '&:hover': { color: 'var(--ink-2)' },
} as const;

/** The steps behind "How this was worked out" (§3, §6): today's step list, collapsed until asked for. */
export function WorkedOut({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <ButtonBase onClick={onToggle} aria-expanded={open} data-testid="worked-out-toggle" sx={LINK_SX}>
      How this was worked out
    </ButtonBase>
  );
}

/**
 * F4 (#1858 §3): one line in plain words while an answer runs — "Finding Gross sales by Customer · step 1 of 2 ·
 * usually about 25 s". The words are the server's (closed templates over this reader's catalog labels); the client
 * adds only the step count and, past the row's p90, swaps the usual time for "taking longer than usual". Before the
 * plan: "Reading your question". The planner's own words, ids and SQL stay behind "How this was worked out".
 */
export default function ProgressLine({ turn }: { turn: LandingAgentTurn }) {
  const [open, setOpen] = React.useState(false);
  const now = progressOf(turn);
  // invariant 7: every running turn on screen can be stopped. The composer's Stop reaches the conversation's running
  // turn (`cancelLandingAgent`: the foreground one, while the conversation runs); a turn it cannot reach — a run left
  // going while a newer question (a Why from a number) finished — carries its own Stop
  const composerReaches = useVoyagerStore((s) => {
    if (s.landingAgentStatus !== 'running') return false;
    const turns = s.landingAgentTurns || [];
    const target = turns.find((t) => t.foreground) || turns[turns.length - 1];
    return target?.id === turn.id;
  });
  const stopRun = useVoyagerStore((s) => s.stopBackgroundAnalysis);
  const [, tick] = React.useState(0);
  const longerAt = now.longerAfter != null ? (turn.sentAt || 0) + now.longerAfter * 1000 : null;
  const longer = longerAt != null && Date.now() > longerAt;
  React.useEffect(() => {
    if (longerAt == null || longer) return undefined;
    const id = window.setTimeout(() => tick((n) => n + 1), Math.max(0, longerAt - Date.now()) + 50);
    return () => window.clearTimeout(id);
  }, [longerAt, longer]);
  if (turn.status !== 'running') return null;
  const tail = longer ? now.longer : now.usually;
  const parts = [now.words, now.step != null && now.of != null ? `step ${now.step} of ${now.of}` : null, tail]
    .filter((p): p is string => !!p);
  return (
    <Box data-testid="progress-line" sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
        {/* a quiet live dot, never a countdown or a percentage */}
        <Box component="span" aria-hidden data-testid="progress-dot" sx={{ width: 7, height: 7, borderRadius: 99, flex: 'none',
          bgcolor: 'var(--accent)', '@media (prefers-reduced-motion: no-preference)': { animation: 'f4live 1.4s ease-in-out infinite' },
          '@keyframes f4live': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.35 } } }} />
        <Box component="span" data-testid="progress-words" aria-live="polite"
             sx={{ fontSize: 13.5, color: 'var(--ink-2)', minWidth: 0 }}>
          {parts.join(' · ')}
        </Box>
        {hasSteps(turn) && <WorkedOut open={open} onToggle={() => setOpen((v) => !v)} />}
        {!composerReaches && turn.analysisOperationId && !turn.stopped && (
          <ButtonBase data-testid="progress-stop" aria-label="Stop this answer" onClick={() => void stopRun(turn.id)}
            sx={{ ...LINK_SX, color: 'var(--ink-2)' }}>Stop</ButtonBase>
        )}
      </Box>
      {open && <WorkingSteps turn={turn} />}
    </Box>
  );
}
